import { html } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import createPanZoom, { PanZoom, PanZoomOptions } from 'panzoom';
import {
  TmDialogElement,
  TmDialogElementStyle,
} from '@thiememeulenhoff/lit-sdk';
import { consume } from '@lit/context';
import {
  LoadingContext,
  loadingStateContext,
} from '../../../contexts/loading.context';
import { tagName } from '../../../helpers';
import { styles } from './image.styles';

const tag = tagName('image-modal');

@customElement(tag)
export class ImageModalComponent extends TmDialogElement {
  static override styles = [TmDialogElementStyle, styles];

  @property({ type: Number })
  maxZoom = 2;

  @property({ type: Number })
  minZoom = 1;

  @query('#full-size-image')
  image!: HTMLImageElement;

  @query('#zoom-out')
  zoomOutButton!: HTMLButtonElement;

  @query('#zoom-in')
  zoomInButton!: HTMLButtonElement;

  // TODO(TLA-9226): remove when print uses its own web components
  @queryAssignedElements({ selector: 'img', slot: 'trigger' })
  triggerImages?: Array<HTMLImageElement>;

  @queryAssignedElements({ selector: 'img' })
  slottedImages!: Array<HTMLImageElement>;

  @state()
  protected panZoom!: PanZoom;

  @state()
  protected panZoomOptions: PanZoomOptions = {
    minZoom: this.minZoom,
    maxZoom: this.maxZoom,
    smoothScroll: false,
    disableKeyboardInteraction: true,
    // This doesn't work as expected when set to `true`
    // See: https://github.com/anvaka/panzoom/issues/112
    bounds: true,
    boundsPadding: 0.01,
    zoomDoubleClickSpeed: 1, // Disable double click
  };

  @consume({ context: loadingStateContext, subscribe: true })
  @property({ attribute: false })
  loadingProvider?: LoadingContext;

  uniqueId = crypto.randomUUID();

  private registered = false;

  constructor() {
    super();
    super.backdropClickEnabled = true;
  }

  override async connectedCallback() {
    super.connectedCallback();

    this.#registerElement();

    await this.updateComplete;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.panZoom) return;
    this.panZoom.dispose();
  }

  override onClose() {
    super.onClose();
    this.panZoom.dispose();
  }

  override extraControls() {
    return html`
      <div class="controls">
        <button
          id="zoom-out"
          class="control-button"
          @click=${this.#zoomOut}
          @touchstart=${this.#zoomOut}
          aria-label="Afbeelding verkleinen"
        >
          <a-icon name="zoom-out"></a-icon>
        </button>
        <button
          id="zoom-in"
          class="control-button"
          @click=${this.#zoomIn}
          @touchstart=${this.#zoomIn}
          aria-label="Afbeelding vergroten"
        >
          <a-icon name="zoom-in"></a-icon>
        </button>
      </div>
    `;
  }

  override open() {
    super.open();
    this.#panZoom();
  }

  #panZoom() {
    this.panZoom = createPanZoom(this.slottedImages[0], this.panZoomOptions);
    this.panZoom.on('transform', (event: PanZoom) => {
      const getTransform = event.getTransform();
      const disableZoomOut = this.panZoom.getMinZoom() === getTransform.scale;
      const disableZoomIn = this.panZoom.getMaxZoom() === getTransform.scale;
      this.zoomOutButton.disabled = disableZoomOut;
      this.zoomInButton.disabled = disableZoomIn;
    });

    const thamId = this.slottedImages[0]
      .getAttribute('src')
      ?.match(/\/(tham\.\w+\..*)\//)?.[1];

    this.dispatchGlobalEvent({
      eventName: 'tmwc-activity',
      detail: {
        type: 'image-modal-open',
        id: thamId,
      },
    });
  }

  #zoomIn() {
    this.panZoom.smoothZoom(
      this.#getHorizontalCenter(),
      this.#getVerticalCenter(),
      1.5,
    );
  }

  #zoomOut() {
    this.panZoom.smoothZoom(
      this.#getHorizontalCenter(),
      this.#getVerticalCenter(),
      0.66,
    );
  }

  #getHorizontalCenter(): number {
    return window.innerWidth / 4;
  }

  #getVerticalCenter(): number {
    return window.innerHeight / 4;
  }

  /*
  Start PoD functionality
   */

  protected override firstUpdated(): void {
    this.#checkLoadingState();
  }

  #checkLoadingState() {
    // TODO(TLA-9226): remove loading provider when print uses its own web components
    if (
      !this.loadingProvider ||
      !this.triggerImages ||
      this.triggerImages.length === 0
    ) {
      return;
    }

    this.#registerElement();

    const image = this.triggerImages[0];
    // force lazy to eager to allow loading state tracking
    if (image.loading === 'lazy') {
      image.loading = 'eager';
    }

    if (image.complete && image.naturalWidth > 0) {
      // Already loaded
      this.#loadHandler();
    } else {
      image.addEventListener('load', () => this.#loadHandler(), { once: true });
    }
  }

  #registerElement() {
    if (this.registered || !this.loadingProvider) return;

    this.loadingProvider.registerElement(this.uniqueId);
    this.registered = true;
  }

  // Why two animation frames? When images are loaded:
  // First frame: sets the dimensions
  // Second frame: commits the pixels
  #loadHandler = async () => {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    this.loadingProvider?.markElementLoaded(this.uniqueId);
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-image-modal': ImageModalComponent;
  }
}
